import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { createProfileForNewUser } from '../lib/profileService';

const APP_GRADIENT = 'linear-gradient(135deg, #6B7FFF 0%, #8B5CF6 100%)';
const BG_GRADIENT = 'linear-gradient(135deg, #e8ecff 0%, #f3e8ff 50%, #ede9fe 100%)';

interface AuthProps {
  onAuthSuccess: (isNewUser?: boolean) => void;
  defaultMode?: 'login' | 'register';
}

export const Auth: React.FC<AuthProps> = ({ onAuthSuccess, defaultMode = 'login' }) => {
  const [isLogin, setIsLogin] = useState(defaultMode === 'login');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const resetFormState = () => {
    setError(null);
    setMessage(null);
    if (isLogin) setAcceptTerms(true);
    else setAcceptTerms(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      if (isLogin) {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });

        if (error) throw error;

        if (data.user) {
          setMessage('Zalogowano pomyślnie! Przekierowywanie...');
          setTimeout(() => onAuthSuccess(false), 600);
        }
      } else {
        const base = process.env.REACT_APP_SITE_URL || window.location.origin;
        const redirectUrl = `${base.replace(/\/$/, '')}/auth/callback`;

        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: redirectUrl,
            data: { full_name: displayName.trim() || undefined }
          }
        });

        if (error) throw error;

        if (data.user) {
          sessionStorage.setItem('justRegisteredUserId', data.user.id);
          if (data.session) {
            console.log('Tworzę profil dla:', data.user.id);
            await createProfileForNewUser(data.user.id, displayName.trim() || null);
          }

          if (data.session) {
            setMessage('Konto utworzone! Automatycznie loguję...');
            setTimeout(() => onAuthSuccess(true), 500);
          } else {
            const { data: sessionData } = await supabase.auth.getSession();
            if (sessionData.session) {
              setMessage('Konto utworzone! Automatycznie loguję...');
              setTimeout(() => onAuthSuccess(true), 500);
            } else {
              setMessage('Konto utworzone! Sprawdź skrzynkę pocztową i kliknij link potwierdzający w emailu. Jeśli email nie przychodzi, sprawdź folder SPAM.');
            }
          }
        }
      }
    } catch (err: any) {
      const msg = err?.message || '';
      if (isLogin) {
        if (msg.toLowerCase().includes('invalid login credentials') || err?.code === 'invalid_credentials') {
          setError('Nieprawidłowy email lub hasło.');
        } else if (msg.toLowerCase().includes('email not confirmed') || err?.code === 'email_not_confirmed') {
          setError('Potwierdź adres email – sprawdź skrzynkę i kliknij link.');
        } else {
          setError(msg || 'Wystąpił błąd logowania.');
        }
      } else {
        setError(msg || 'Wystąpił błąd');
      }
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = isLogin
    ? true
    : acceptTerms;

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '14px 16px',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    fontSize: '16px',
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.2s'
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: BG_GRADIENT,
        padding: '24px'
      }}
    >
      {/* Logo + nazwa nad kartą */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '24px'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '14px',
          background: APP_GRADIENT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(107, 127, 255, 0.35)'
        }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.5 20.5L3.5 13.5a5 5 0 117.07-7.07l9.9 9.9a5 5 0 11-7.07 7.07L10.5 20.5z"/>
            <path d="M8.5 8.5l7 7"/>
          </svg>
        </div>
        <h1 style={{
          margin: 0,
          fontSize: '24px',
          fontWeight: 700,
          color: '#1e293b'
        }}>
          IBD Tracker
        </h1>
      </div>

      {/* Karta */}
      <div style={{
        backgroundColor: 'white',
        padding: '32px',
        borderRadius: '24px',
        boxShadow: '0 8px 32px rgba(15, 23, 42, 0.12), 0 2px 8px rgba(15, 23, 42, 0.06)',
        width: '100%',
        maxWidth: '400px'
      }}>
        {/* Taby */}
        <div style={{
          display: 'flex',
          gap: '4px',
          marginBottom: '28px',
          padding: '4px',
          background: '#f1f5f9',
          borderRadius: '14px'
        }}>
          <button
            type="button"
            onClick={() => {
              setIsLogin(true);
              resetFormState();
            }}
            style={{
              flex: 1,
              padding: '12px 16px',
              border: 'none',
              borderRadius: '12px',
              fontSize: '15px',
              fontWeight: 600,
              cursor: 'pointer',
              background: isLogin ? APP_GRADIENT : 'transparent',
              color: isLogin ? 'white' : '#64748b',
              boxShadow: isLogin ? '0 2px 8px rgba(107, 127, 255, 0.3)' : 'none'
            }}
          >
            Zaloguj się
          </button>
          <button
            type="button"
            onClick={() => {
              setIsLogin(false);
              resetFormState();
            }}
            style={{
              flex: 1,
              padding: '12px 16px',
              border: 'none',
              borderRadius: '12px',
              fontSize: '15px',
              fontWeight: 600,
              cursor: 'pointer',
              background: !isLogin ? APP_GRADIENT : 'transparent',
              color: !isLogin ? 'white' : '#64748b',
              boxShadow: !isLogin ? '0 2px 8px rgba(107, 127, 255, 0.3)' : 'none'
            }}
          >
            Zarejestruj się
          </button>
        </div>

        {error && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#fef2f2',
            color: '#dc2626',
            borderRadius: '12px',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {message && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#f0fdf4',
            color: '#16a34a',
            borderRadius: '12px',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {message}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {!isLogin && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#475569', fontSize: '14px' }}>
                Imię
              </label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                style={inputStyle}
                placeholder="np. Ania"
              />
            </div>
          )}

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#475569', fontSize: '14px' }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={inputStyle}
              placeholder="twoj@email.com"
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#475569', fontSize: '14px' }}>
              Hasło
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              style={inputStyle}
              placeholder="Minimum 6 znaków"
            />
          </div>

          {!isLogin && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#475569'
              }}>
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  style={{ marginTop: '4px', flexShrink: 0 }}
                />
                <span>
                  Rejestrując się, akceptuję{' '}
                  <a href="/regulamin" style={{ color: '#6B7FFF', textDecoration: 'underline' }}>Regulamin</a>
                  {' oraz '}
                  <a href="/polityka-prywatnosci" style={{ color: '#6B7FFF', textDecoration: 'underline' }}>Politykę prywatności</a>
                </span>
              </label>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !canSubmit}
            style={{
              width: '100%',
              padding: '14px',
              background: loading || !canSubmit ? '#cbd5e1' : APP_GRADIENT,
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: 600,
              cursor: loading || !canSubmit ? 'not-allowed' : 'pointer',
              boxShadow: loading || !canSubmit ? 'none' : '0 4px 12px rgba(107, 127, 255, 0.35)'
            }}
          >
            {loading ? 'Ładowanie...' : (isLogin ? 'Zaloguj się' : 'Zarejestruj się')}
          </button>

          {isLogin && (
            <div style={{ textAlign: 'center', marginTop: '16px' }}>
              <Link
                to="/forgot-password"
                style={{
                  color: '#6B7FFF',
                  textDecoration: 'none',
                  fontSize: '14px'
                }}
              >
                Zapomniałem/am hasła
              </Link>
            </div>
          )}
        </form>

        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <span style={{ color: '#64748b', fontSize: '14px' }}>
            {isLogin ? (
              <>
                Nie masz konta?{' '}
                <button
                  type="button"
                  onClick={() => {
                    setIsLogin(false);
                    resetFormState();
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#6B7FFF',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontSize: '14px',
                    fontWeight: 600
                  }}
                >
                  Zarejestruj się
                </button>
              </>
            ) : (
              <>
                Masz już konto?{' '}
                <button
                  type="button"
                  onClick={() => {
                    setIsLogin(true);
                    resetFormState();
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#6B7FFF',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontSize: '14px',
                    fontWeight: 600
                  }}
                >
                  Zaloguj się
                </button>
              </>
            )}
          </span>
        </div>
      </div>
    </div>
  );
};
